import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MapPin, Shield, BarChart3, Lock, Info, Check, X } from 'lucide-react';
import { toast } from 'sonner';

export const LocationConsentDialog = () => {
  const { user } = useAuth();
  const { profile, updateProfile } = useUserProfile();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Show dialog if consent not yet set (NULL)
    if (user && profile && profile.location_sharing_consent === null) {
      setOpen(true);
    }
  }, [user, profile]);

  const handleConsent = async (consent: boolean) => {
    const result = await updateProfile({
      location_sharing_consent: consent,
      location_consent_updated_at: new Date().toISOString()
    } as any);
    
    if (!result.error) {
      toast.success(
        consent 
          ? 'Location sharing enabled for security monitoring'
          : 'Location sharing disabled. Your location will remain private.'
      );
      setOpen(false);
    } else {
      toast.error('Failed to save preference. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Share Your Location?
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            We'd like to track your country and city for:
          </p>
          
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 text-green-500" />
              <span><strong>Security:</strong> Detect suspicious logins from unusual locations</span>
            </li>
            <li className="flex items-start gap-2">
              <BarChart3 className="w-4 h-4 mt-0.5 text-blue-500" />
              <span><strong>Analytics:</strong> Help us understand our global community</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 mt-0.5 text-purple-500" />
              <span><strong>Privacy:</strong> Only country and city are stored, IP is anonymized</span>
            </li>
          </ul>
          
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              You can change this preference anytime in your security settings.
            </AlertDescription>
          </Alert>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => handleConsent(false)}
            className="flex-1"
          >
            <X className="w-4 h-4 mr-2" />
            No, Keep Private
          </Button>
          <Button 
            onClick={() => handleConsent(true)}
            className="flex-1"
          >
            <Check className="w-4 h-4 mr-2" />
            Yes, Share Location
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
